/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — edit-session bookkeeping (People Rebuild §0/§3,
   Josh grant 2026-08-17: "edit-state + save model").

   This hook owns ONLY the session lifecycle (active/saving/error) and
   the DIFF bookkeeping — which member/edge/junction ids changed this
   session, event-driven (every mutation call site "notes" itself here
   the moment it happens, instead of deep-diffing two snapshots at
   commit time). It deliberately does NOT own the actual chart data —
   positions live in React Flow `nodes` state, edges/junctions live in
   React Flow `edges`/`nodes` state — those are already fully local
   once a session starts (no network echo needed), so the ONLY extra
   state a session needs is:
     - which member ids' positions moved (`positionsTouched`)
     - which member ids got a new effective manager (`reportsToOverrides`,
       the reports-to MIRROR — the full commit value, not a delta)
     - which edge/junction ids were created / patched / deleted, so a
       create-then-delete-same-session nets out to nothing sent, and a
       delete of a pre-existing row becomes a real `deleteIds` entry.

   `buildCommitPayload` reads the CURRENT React Flow nodes/edges at
   the moment Save is pressed and reconstructs full row shapes for
   every id these bookkeeping sets reference — see `edgeFromRF` /
   `junctionFromRF` below.
   ═══════════════════════════════════════════════════════════════ */

import { useCallback, useRef, useState } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type { OrgChartEdge, OrgChartJunction, OrgChartNote } from '../../../../../shared/entities';
import type { OrgChartCommitPayload } from '../commitOrgChart';
import { parseEndpoint, type NoteNodeData, type OrgEdgeData } from '../types';

/** Reconstruct a full `OrgChartEdge` row from a React Flow edge — the
 *  edit-session model's single source of truth for edge data while a
 *  session is open. Exported: also used by OrgChartV2 for undo
 *  snapshots (edge-delete, edge-patch, junction-delete cascades) so
 *  every reconstruction agrees on the same field mapping. */
export function edgeFromRF(e: Edge, id: string): OrgChartEdge | null {
  if (!e.source || !e.target) return null;
  const d = (e.data || {}) as OrgEdgeData;
  const src = parseEndpoint(e.source);
  const tgt = parseEndpoint(e.target);
  const now = new Date().toISOString();
  const styleObj = (e.style || {}) as { stroke?: string; strokeWidth?: number };
  return {
    id,
    sourceMemberId: src.id,
    targetMemberId: tgt.id,
    sourceType: src.type,
    targetType: tgt.type,
    style: d.lineStyle || 'solid',
    color: d.color,
    thickness: typeof styleObj.strokeWidth === 'number' ? styleObj.strokeWidth : undefined,
    label: d.label,
    sourceHandle: (e.sourceHandle as OrgChartEdge['sourceHandle']) || undefined,
    targetHandle: (e.targetHandle as OrgChartEdge['targetHandle']) || undefined,
    waypoints: d.waypoints && d.waypoints.length > 0 ? d.waypoints : undefined,
    // Ignored server-side (org_chart_edges INSERT/UPDATE both stamp
    // now() unconditionally) — populated only to satisfy the type.
    createdAt: now,
    updatedAt: now,
  };
}

function junctionFromRF(n: Node, id: string): OrgChartJunction {
  const dd = (n.data || {}) as { label?: string; color?: string };
  const now = new Date().toISOString();
  return {
    id,
    x: n.position.x,
    y: n.position.y,
    label: dd.label,
    color: dd.color,
    createdAt: now,
    updatedAt: now,
  };
}

/** Reconstruct a full `OrgChartNote` row from a React Flow `textBox`
 *  node — exported: also used by OrgChartV2 for undo snapshots
 *  (note-delete, note-patch), same field-mapping-agreement reason as
 *  `edgeFromRF`. */
export function noteFromRF(n: Node, id: string): OrgChartNote {
  const dd = (n.data || {}) as NoteNodeData;
  const now = new Date().toISOString();
  return {
    id,
    x: n.position.x,
    y: n.position.y,
    width: dd.width,
    text: dd.text ?? '',
    fontSize: dd.fontSize ?? 14,
    color: dd.color,
    createdAt: now,
    updatedAt: now,
  };
}

export function useEditSession() {
  const [active, setActive] = useState(false);
  const [dirtyTick, setDirtyTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalChangeNotice, setExternalChangeNotice] = useState(false);

  const positionsTouched = useRef<Set<string>>(new Set());
  const reportsToOverrides = useRef<Map<string, string>>(new Map());
  // Team Management UX Overhaul (2026-08-18): on-canvas person-card <->
  // team-box drag staging (Main board only — callers gate that). Keyed
  // `${teamId}::${memberId}` so re-dragging the same pair back and forth
  // in one session cancels to a true no-op (same create-then-delete-
  // same-session pattern edges/junctions/notes already use below) instead
  // of sending a redundant remove-then-add that would silently reset the
  // member's role or regenerate the assignment row.
  const membershipAdds = useRef<
    Map<string, { teamId: string; memberId: string; role: 'leader' | 'member' }>
  >(new Map());
  const membershipRemoves = useRef<Map<string, { teamId: string; memberId: string }>>(new Map());
  // Team-box-onto-team-box reparent staging — teamId -> requested new
  // parentTeamId (null = un-nest). A Map naturally keeps only the LATEST
  // requested parent per team if the box is dragged more than once.
  const teamParentChanges = useRef<Map<string, string | null>>(new Map());
  const newEdgeIds = useRef<Set<string>>(new Set());
  const upsertedEdgeIds = useRef<Set<string>>(new Set());
  const deletedEdgeIds = useRef<Set<string>>(new Set());
  const newJunctionIds = useRef<Set<string>>(new Set());
  const upsertedJunctionIds = useRef<Set<string>>(new Set());
  const deletedJunctionIds = useRef<Set<string>>(new Set());
  const newNoteIds = useRef<Set<string>>(new Set());
  const upsertedNoteIds = useRef<Set<string>>(new Set());
  const deletedNoteIds = useRef<Set<string>>(new Set());
  const removedMemberIds = useRef<Set<string>>(new Set());

  const bump = useCallback(() => setDirtyTick((v) => v + 1), []);

  const resetBookkeeping = useCallback(() => {
    positionsTouched.current.clear();
    reportsToOverrides.current.clear();
    membershipAdds.current.clear();
    membershipRemoves.current.clear();
    teamParentChanges.current.clear();
    newEdgeIds.current.clear();
    upsertedEdgeIds.current.clear();
    deletedEdgeIds.current.clear();
    newJunctionIds.current.clear();
    upsertedJunctionIds.current.clear();
    deletedJunctionIds.current.clear();
    newNoteIds.current.clear();
    upsertedNoteIds.current.clear();
    deletedNoteIds.current.clear();
    removedMemberIds.current.clear();
  }, []);

  const begin = useCallback(() => {
    resetBookkeeping();
    setError(null);
    setExternalChangeNotice(false);
    setDirtyTick(0);
    setActive(true);
  }, [resetBookkeeping]);

  /** Exit the session, discarding all bookkeeping. Used both for a
   *  confirmed Cancel and for the post-commit-success exit — the
   *  caller is responsible for restoring/refreshing the visible
   *  nodes/edges appropriately in each case. */
  const end = useCallback(() => {
    resetBookkeeping();
    setError(null);
    setExternalChangeNotice(false);
    setActive(false);
  }, [resetBookkeeping]);

  const markPositionsTouched = useCallback(
    (ids: string[]) => {
      for (const id of ids) positionsTouched.current.add(id);
      bump();
    },
    [bump]
  );

  const setReportsTo = useCallback(
    (memberId: string, value: string) => {
      reportsToOverrides.current.set(memberId, value);
      bump();
    },
    [bump]
  );

  const clearReportsToOverride = useCallback(
    (memberId: string) => {
      reportsToOverrides.current.delete(memberId);
      bump();
    },
    [bump]
  );

  /** Stage "add memberId to teamId" — dragging a person card into a team
   *  box (Main board only). Exact-cancels a pending remove for the same
   *  pair instead of queuing both (see the refs' comment above). */
  const stageMembershipAdd = useCallback(
    (teamId: string, memberId: string, role: 'leader' | 'member' = 'member') => {
      const key = `${teamId}::${memberId}`;
      if (membershipRemoves.current.has(key)) {
        membershipRemoves.current.delete(key);
      } else {
        membershipAdds.current.set(key, { teamId, memberId, role });
      }
      bump();
    },
    [bump]
  );

  /** Stage "remove memberId from teamId" — dragging a card that IS in a
   *  team box fully outside it. */
  const stageMembershipRemove = useCallback(
    (teamId: string, memberId: string) => {
      const key = `${teamId}::${memberId}`;
      if (membershipAdds.current.has(key)) {
        membershipAdds.current.delete(key);
      } else {
        membershipRemoves.current.set(key, { teamId, memberId });
      }
      bump();
    },
    [bump]
  );

  /** Stage a team-box reparent (nest under another box) or un-nest
   *  (`parentTeamId: null`) — dragging a team box fully inside/outside
   *  another. */
  const stageTeamParentChange = useCallback(
    (teamId: string, parentTeamId: string | null) => {
      teamParentChanges.current.set(teamId, parentTeamId);
      bump();
    },
    [bump]
  );

  const noteEdgeCreated = useCallback(
    (id: string) => {
      newEdgeIds.current.add(id);
      upsertedEdgeIds.current.add(id);
      deletedEdgeIds.current.delete(id);
      bump();
    },
    [bump]
  );
  const noteEdgeUpdated = useCallback(
    (id: string) => {
      upsertedEdgeIds.current.add(id);
      bump();
    },
    [bump]
  );
  const noteEdgeDeleted = useCallback(
    (id: string) => {
      if (newEdgeIds.current.has(id)) {
        // Created and deleted inside the same session — the server
        // never saw it, so there is nothing to send.
        newEdgeIds.current.delete(id);
        upsertedEdgeIds.current.delete(id);
      } else {
        upsertedEdgeIds.current.delete(id);
        deletedEdgeIds.current.add(id);
      }
      bump();
    },
    [bump]
  );

  /** Is this (pre-existing, i.e. NOT session-created) edge id currently
   *  queued for deletion this session? Duplicate-creation-path fix
   *  (2026-08-19): `onConnect` calls this before minting a brand-new id
   *  for a redrawn connection — deleting an edge immediately drops it
   *  from React Flow's local `edges` state (see `sessionDeleteEdge`), so
   *  without this check, redrawing the SAME connection in the SAME
   *  session looked indistinguishable from a genuinely new relationship,
   *  producing two ids for one (source, target) pair in a single commit
   *  payload — the exact shape that crashed `orgchart-commit`
   *  (`duplicate key value violates unique constraint
   *  "org_chart_edges_uniq_view"`, live 2026-08-19). */
  const isEdgePendingDelete = useCallback((id: string) => deletedEdgeIds.current.has(id), []);

  /** Un-delete a pending-delete edge id and mark it dirty again — the
   *  "redraw what you just deleted" case `onConnect` detects via
   *  `isEdgePendingDelete`. Deliberately NOT routed through
   *  `noteEdgeCreated`: that also adds the id to `newEdgeIds`, which
   *  would make a LATER delete-in-this-session of this same id look like
   *  "created and deleted this session, nothing to send" — silently
   *  keeping the pre-existing server row alive against the user's actual
   *  final intent. This id already exists server-side; reviving it is an
   *  update, not a create. */
  const noteEdgeRevived = useCallback(
    (id: string) => {
      deletedEdgeIds.current.delete(id);
      upsertedEdgeIds.current.add(id);
      bump();
    },
    [bump]
  );

  const noteJunctionCreated = useCallback(
    (id: string) => {
      newJunctionIds.current.add(id);
      upsertedJunctionIds.current.add(id);
      deletedJunctionIds.current.delete(id);
      bump();
    },
    [bump]
  );
  const noteJunctionUpdated = useCallback(
    (id: string) => {
      upsertedJunctionIds.current.add(id);
      bump();
    },
    [bump]
  );
  const noteJunctionDeleted = useCallback(
    (id: string) => {
      if (newJunctionIds.current.has(id)) {
        newJunctionIds.current.delete(id);
        upsertedJunctionIds.current.delete(id);
      } else {
        upsertedJunctionIds.current.delete(id);
        deletedJunctionIds.current.add(id);
      }
      bump();
    },
    [bump]
  );

  const noteNoteCreated = useCallback(
    (id: string) => {
      newNoteIds.current.add(id);
      upsertedNoteIds.current.add(id);
      deletedNoteIds.current.delete(id);
      bump();
    },
    [bump]
  );
  const noteNoteUpdated = useCallback(
    (id: string) => {
      upsertedNoteIds.current.add(id);
      bump();
    },
    [bump]
  );
  const noteNoteDeleted = useCallback(
    (id: string) => {
      if (newNoteIds.current.has(id)) {
        newNoteIds.current.delete(id);
        upsertedNoteIds.current.delete(id);
      } else {
        upsertedNoteIds.current.delete(id);
        deletedNoteIds.current.add(id);
      }
      bump();
    },
    [bump]
  );

  /** The one exception (remove-from-chart) already committed itself
   *  immediately server-side — this just reconciles local bookkeeping
   *  so Save doesn't resurrect the removed member's stray position or
   *  reports-to override. */
  const noteMemberRemoved = useCallback(
    (id: string) => {
      removedMemberIds.current.add(id);
      positionsTouched.current.delete(id);
      reportsToOverrides.current.delete(id);
      bump();
    },
    [bump]
  );

  const noteExternalChange = useCallback(() => setExternalChangeNotice(true), []);

  const buildCommitPayload = useCallback((nodes: Node[], edges: Edge[]): OrgChartCommitPayload => {
    const edgeById = new Map(
      edges
        .filter((e) => e.type === 'org')
        .map((e) => [((e.data || {}) as OrgEdgeData).edgeId || e.id, e] as const)
    );
    const junctionNodeById = new Map(
      nodes
        .filter((n) => n.type === 'junction')
        .map((n) => [n.id.replace(/^junction-/, ''), n] as const)
    );
    const noteNodeById = new Map(
      nodes.filter((n) => n.type === 'textBox').map((n) => [n.id.replace(/^note-/, ''), n] as const)
    );
    const nodeById = new Map(nodes.map((n) => [n.id, n] as const));

    const positions = Array.from(positionsTouched.current)
      .map((id) => {
        const n = nodeById.get(id);
        return n ? { id, orgX: n.position.x, orgY: n.position.y } : null;
      })
      .filter((x): x is { id: string; orgX: number; orgY: number } => x !== null);

    const reportsTo = Array.from(reportsToOverrides.current.entries()).map(([id, v]) => ({
      id,
      reportsTo: v,
    }));

    const edgeUpsert = Array.from(upsertedEdgeIds.current)
      .map((id) => {
        const e = edgeById.get(id);
        return e ? edgeFromRF(e, id) : null;
      })
      .filter((x): x is OrgChartEdge => x !== null);

    const junctionUpsert = Array.from(upsertedJunctionIds.current)
      .map((id) => {
        const n = junctionNodeById.get(id);
        return n ? junctionFromRF(n, id) : null;
      })
      .filter((x): x is OrgChartJunction => x !== null);

    const noteUpsert = Array.from(upsertedNoteIds.current)
      .map((id) => {
        const n = noteNodeById.get(id);
        return n ? noteFromRF(n, id) : null;
      })
      .filter((x): x is OrgChartNote => x !== null);

    const teamParentChangesArr = Array.from(teamParentChanges.current.entries()).map(
      ([teamId, parentTeamId]) => ({ teamId, parentTeamId })
    );

    return {
      positions,
      reportsTo,
      edges: { upsert: edgeUpsert, deleteIds: Array.from(deletedEdgeIds.current) },
      junctions: { upsert: junctionUpsert, deleteIds: Array.from(deletedJunctionIds.current) },
      notes: { upsert: noteUpsert, deleteIds: Array.from(deletedNoteIds.current) },
      membership: {
        adds: Array.from(membershipAdds.current.values()),
        removes: Array.from(membershipRemoves.current.values()),
      },
      teamParentChanges: teamParentChangesArr,
    };
  }, []);

  const dirtyCount =
    positionsTouched.current.size +
    reportsToOverrides.current.size +
    upsertedEdgeIds.current.size +
    deletedEdgeIds.current.size +
    upsertedJunctionIds.current.size +
    deletedJunctionIds.current.size +
    upsertedNoteIds.current.size +
    deletedNoteIds.current.size +
    removedMemberIds.current.size +
    membershipAdds.current.size +
    membershipRemoves.current.size +
    teamParentChanges.current.size;
  // dirtyTick has no direct reader beyond forcing this recompute on
  // every bump() — referenced so eslint doesn't flag it as unused.
  void dirtyTick;

  return {
    active,
    dirtyCount,
    saving,
    setSaving,
    error,
    setError,
    externalChangeNotice,
    noteExternalChange,
    begin,
    end,
    markPositionsTouched,
    setReportsTo,
    clearReportsToOverride,
    reportsToOverrides: reportsToOverrides.current,
    stageMembershipAdd,
    stageMembershipRemove,
    membershipAdds: membershipAdds.current,
    membershipRemoves: membershipRemoves.current,
    stageTeamParentChange,
    teamParentChanges: teamParentChanges.current,
    noteEdgeCreated,
    noteEdgeUpdated,
    noteEdgeDeleted,
    isEdgePendingDelete,
    noteEdgeRevived,
    noteJunctionCreated,
    noteJunctionUpdated,
    noteJunctionDeleted,
    noteNoteCreated,
    noteNoteUpdated,
    noteNoteDeleted,
    noteMemberRemoved,
    removedMemberIds: removedMemberIds.current,
    buildCommitPayload,
  };
}

export type EditSession = ReturnType<typeof useEditSession>;
